import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import apiService from '../../services/api';
import attendanceApi, { AttendanceEntry, MonthlySummary } from '../../services/attendanceApi';
import KpiRow, { Kpi } from './KpiRow';
import AttendanceCalendar from './AttendanceCalendar';
import { EmployeeOption, StatusBadge, ShiftLabel, fmtTime, rupee, hasSalarySetup } from './shared';

const SalaryTab: React.FC<{ employees: EmployeeOption[] }> = ({ employees }) => {
  const [employeeId, setEmployeeId] = useState<string>('');
  const [month, setMonth] = useState<string>(dayjs().format('YYYY-MM'));
  const [search, setSearch] = useState<string>('');

  const roster = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((e) => {
      if (!hasSalarySetup(e)) return false;
      if (!q) return true;
      return (e.user?.name || '').toLowerCase().includes(q) || (e.designation || '').toLowerCase().includes(q);
    });
  }, [employees, search]);

  // Auto-select the first employee that actually has a salary set up.
  useEffect(() => {
    if (!employeeId) {
      const first = employees.find(hasSalarySetup);
      if (first) setEmployeeId(String(first.id));
    }
  }, [employeeId, employees]);

  const selected = employees.find((e) => String(e.id) === employeeId);
  const monthStart = `${month}-01`;
  const monthEnd = dayjs(monthStart).endOf('month').format('YYYY-MM-DDTHH:mm:ss');

  const { data: summaryData, isLoading: loadingSummary } = useQuery({
    queryKey: ['attendance', 'summary', employeeId, month],
    queryFn: () => attendanceApi.getSummary(Number(employeeId), month),
    enabled: !!employeeId,
  });
  const { data: recordsData, isLoading: loadingRecords } = useQuery({
    queryKey: ['attendance', 'staff-records', employeeId, month],
    queryFn: () => attendanceApi.getRecords({ employeeId: Number(employeeId), fromDate: monthStart, toDate: monthEnd }),
    enabled: !!employeeId,
  });
  // Outstanding advances (not month-scoped): shown for reference, settled separately at withdrawal.
  const { data: advancesData } = useQuery({
    queryKey: ['salary', 'advances', employeeId],
    queryFn: () => apiService.get(`/salary/advances/${employeeId}`),
    enabled: !!employeeId,
  });

  const summary: MonthlySummary | null = summaryData?.summary ?? null;
  const records: AttendanceEntry[] = recordsData?.entries ?? [];
  const outstandingAdvance: number = advancesData?.summary?.remainingAdvances ?? 0;

  const kpis: Kpi[] = [
    { label: 'On payroll', value: roster.length, note: 'staff with a pay rule' },
    { label: 'This karigar', value: rupee(summary?.computedSalary), note: selected?.user?.name || '—' },
    { label: 'Extras approved', value: rupee(summary?.extrasAmount), note: `${summary?.extraQuantity ?? 0} × ${rupee(summary?.extraRate)}`, tone: (summary?.extrasAmount || 0) > 0 ? 'good' : 'default' },
    { label: 'Period', value: dayjs(monthStart).format('MMM YYYY'), note: 'base prorated by days present' },
  ];

  const gotoMonth = (dir: -1 | 1) => setMonth(dayjs(monthStart).add(dir, 'month').format('YYYY-MM'));

  return (
    <div>
      <KpiRow items={kpis} />

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5 items-start">
        {/* ── Payroll roster ── */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-3 py-2.5 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search karigar…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="max-h-60 lg:max-h-[70vh] overflow-y-auto divide-y divide-gray-100">
            {roster.length === 0 ? (
              <div className="text-center text-gray-400 text-sm py-10">No karigars found.</div>
            ) : roster.map((emp) => {
              const on = String(emp.id) === employeeId;
              return (
                <button
                  key={emp.id}
                  onClick={() => setEmployeeId(String(emp.id))}
                  className={`w-full flex items-center justify-between gap-2 px-4 py-3 text-left transition-colors border-l-2 ${on ? 'bg-blue-50 border-blue-600' : 'border-transparent hover:bg-gray-50'}`}
                >
                  <span className="min-w-0">
                    <span className={`block text-sm font-medium truncate ${on ? 'text-blue-700' : 'text-gray-900'}`}>{emp.user?.name || `Employee #${emp.id}`}</span>
                    {emp.designation && <span className="block text-xs text-gray-500 truncate">{emp.designation}</span>}
                  </span>
                  <span className="text-sm font-semibold text-gray-700 shrink-0">{emp.type === 'METER' ? 'Meter' : rupee(emp.rate || 0)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Detail ── */}
        <div className="min-w-0">
          {!employeeId ? (
            <div className="bg-white rounded-xl border border-dashed border-gray-200 text-center text-gray-400 py-16">
              Select a karigar from the list to see their monthly salary and attendance.
            </div>
          ) : (
            <div className="space-y-5">
              {/* Header: name + month nav + total payable */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-gray-900 truncate">{selected?.user?.name || 'Karigar'}</h2>
                  <p className="text-sm text-gray-500 truncate">{selected?.designation || 'Staff'}{selected?.user?.email ? ` · ${selected.user.email}` : ''}</p>
                </div>
                <div className="flex items-center gap-4 sm:gap-6 w-full sm:w-auto">
                  {/* Month picker: full-width on mobile, compact on desktop */}
                  <div className="flex items-center justify-between rounded-md border border-gray-300 bg-white flex-1 sm:flex-none">
                    <button onClick={() => gotoMonth(-1)} className="px-2.5 py-2 text-gray-600 hover:bg-gray-50 rounded-l-md" title="Previous month"><ChevronLeft className="h-4 w-4" /></button>
                    <span className="px-2 py-2 text-sm font-medium text-gray-800 text-center flex-1 sm:flex-none sm:min-w-[130px]">{dayjs(monthStart).format('MMMM YYYY')}</span>
                    <button onClick={() => gotoMonth(1)} className="px-2.5 py-2 text-gray-600 hover:bg-gray-50 rounded-r-md" title="Next month"><ChevronRight className="h-4 w-4" /></button>
                  </div>
                  {/* Total payable — hidden on mobile (shown large in the Payable card below) */}
                  {summary && (
                    <div className="hidden sm:block text-right shrink-0">
                      <div className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">Total payable</div>
                      <div className="text-xl font-bold text-blue-600 whitespace-nowrap">{rupee(summary.computedSalary)}</div>
                    </div>
                  )}
                </div>
              </div>

              {loadingSummary ? (
                <div className="bg-white rounded-xl border border-gray-200 text-center text-gray-400 py-12">Loading…</div>
              ) : !summary ? (
                <div className="bg-white rounded-xl border border-gray-200 text-center text-gray-400 py-12">No data for this month.</div>
              ) : (
                <>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
                    {/* Payable breakdown */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="text-sm font-semibold text-gray-800 mb-4">Payable — {dayjs(monthStart).format('MMMM YYYY')}</h3>
                      <div className="text-3xl font-bold text-gray-900 mb-4">{rupee(summary.computedSalary)}</div>
                      <div className="space-y-3 text-sm border-t border-gray-100 pt-4">
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium text-gray-700">{summary.payType === 'METER' ? 'Meter earnings' : 'Monthly base'}</div>
                            <div className="text-xs text-gray-500">{summary.payType === 'METER' ? `${summary.meters} m × rate` : `${rupee(summary.monthlyRate ?? 0)} ÷ 30 × ${summary.presentDays} present`}</div>
                          </div>
                          <span className="font-medium text-gray-900 whitespace-nowrap">{rupee(summary.basePay)}</span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium text-gray-700">Extra {summary.extraUnit === 'HOUR' ? 'hours' : 'machines'}</div>
                            <div className="text-xs text-gray-500">{summary.extraQuantity} approved × {rupee(summary.extraRate)}</div>
                          </div>
                          <span className="font-medium text-gray-900 whitespace-nowrap">{rupee(summary.extrasAmount)}</span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium text-gray-700">Advances</div>
                            <div className="text-xs text-gray-500">{outstandingAdvance > 0 ? 'Outstanding · settled at withdrawal' : 'None outstanding'}</div>
                          </div>
                          <span className="font-medium text-gray-900 whitespace-nowrap">{rupee(outstandingAdvance)}</span>
                        </div>
                        <div className="border-t border-gray-200 pt-3 flex items-center justify-between gap-3">
                          <span className="font-semibold text-gray-800">Total payable</span>
                          <span className="text-lg font-bold text-blue-600 whitespace-nowrap">{rupee(summary.computedSalary)}</span>
                        </div>
                      </div>
                      <div className="mt-4 px-3 py-2.5 bg-gray-50 border-l-2 border-amber-500 text-xs text-gray-600 leading-relaxed">
                        Base pay is the monthly rate ÷ 30 × days present, so absences reduce it. Approved extra-machine runs are added on top.
                      </div>
                    </div>

                    {/* Attendance calendar */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-800">Attendance — {dayjs(monthStart).format('MMMM YYYY')}</h3>
                      </div>
                      <AttendanceCalendar month={month} summary={summary} showStats />
                    </div>
                  </div>

                  {/* Shift log */}
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
                      <h3 className="text-sm font-semibold text-gray-800">Shift log</h3>
                      <span className="text-xs text-gray-500">{records.filter((r) => r.status === 'PENDING').length} awaiting approval</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            {['Date', 'Shift', 'In / Out', 'Status'].map((h) => (
                              <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {loadingRecords ? (
                            <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                          ) : records.length === 0 ? (
                            <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No attendance this month.</td></tr>
                          ) : records.map((e) => (
                            <tr key={e.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{dayjs(e.clockInAt).format('DD MMM YYYY')}</td>
                              <td className="px-4 py-3"><ShiftLabel shift={e.shiftType} /></td>
                              <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{fmtTime(e.clockInAt)} → {fmtTime(e.clockOutAt)}</td>
                              <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalaryTab;
